import React, { useState, useEffect } from 'react';
import { SvgIcon } from 'ui/SvgIcon';
import KeyboardArrowDown from 'ui/Icons/keyboard_arrow_down.component.svg';
import DownloadIcon from 'ui/Icons/tabler/download.component.svg';
import { useCurrentWidth } from 'effects';
import { IExploreHotelDocumentItem } from 'services/BackendApi/types/HotelCollectionDetailResponse';

interface IHotelDocumentsProps {
  documents: IExploreHotelDocumentItem[];
}

/**
 * HotelDocuments Component
 * Renders the hotel documents section with a list view and PDF preview.
 * - Desktop: Two-column layout with document list on left and PDF preview on right
 * - Mobile: Single column with documents opening in new tab
 */
export const HotelDocuments: React.FC<IHotelDocumentsProps> = ({ documents }) => {
  const { isLgOrLarger } = useCurrentWidth();
  const [selectedDocumentUuid, setSelectedDocumentUuid] = useState<string | null>(null);

  // Auto-select first document on desktop when component mounts
  useEffect(() => {
    if (documents.length > 0 && isLgOrLarger && !selectedDocumentUuid) {
      setSelectedDocumentUuid(documents[0].uuid);
    }
  }, [documents, isLgOrLarger, selectedDocumentUuid]);

  const selectedDocument = documents.find(d => d.uuid === selectedDocumentUuid);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-[20px]">
      {/* Left Column: Document List */}
      <div
        className="flex flex-col max-h-[500px] overflow-y-scroll bg-white-hard"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#C1C1C1 #F1F1F1' }}
      >
        {documents.map(doc => {
          const isSelected = selectedDocumentUuid === doc.uuid;
          const handleClick = () => {
            if (isLgOrLarger) {
              setSelectedDocumentUuid(doc.uuid);
            }
          };

          // Mobile: Use anchor tag to open in new tab
          if (!isLgOrLarger) {
            return (
              <a
                key={doc.uuid}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-[20px] py-[5px] px-[20px] bg-white border border-gray-20 font-pt-sans text-[15px] leading-[1.294em] text-black hover:bg-gray-10 transition-all"
              >
                <span className="flex-1">{doc.displayName}</span>
                <SvgIcon IconComponent={KeyboardArrowDown} className="w-[24px] h-[24px] fill-black -rotate-90" />
              </a>
            );
          }

          // Desktop: Clickable div for preview
          return (
            <div
              key={doc.uuid}
              onClick={handleClick}
              className={`flex items-center gap-[20px] py-[5px] pl-20px pr-10px cursor-pointer transition-all font-pt-sans text-[15px] leading-[1.294em] text-black border border-gray-20 ${
                isSelected ? 'font-bold bg-gray-10' : 'font-normal bg-white hover:bg-gray-10'
              }`}
            >
              <span className="flex-1">{doc.displayName}</span>
              <SvgIcon IconComponent={KeyboardArrowDown} className="w-[24px] h-[24px] fill-black -rotate-90" />
            </div>
          );
        })}
      </div>

      {/* Right Column: PDF Preview (Desktop only) */}
      {isLgOrLarger && selectedDocument && (
        <div className="relative max-h-[500px] bg-gray-20 rounded-[2px] border border-gray-20 p-20px">
          <iframe
            src={`${selectedDocument.url}#toolbar=0&view=FitH`}
            className="w-full h-[460px] rounded-[2px]"
            title={selectedDocument.displayName}
          />
          {/* Floating Download Button */}
          <button
            onClick={() => {
              window.open(selectedDocument.url, '_blank');
            }}
            className="absolute bottom-[20px] right-[20px] flex items-center justify-center w-[50px] h-[50px] bg-brown-prime hover:bg-brown-120 rounded-full transition-colors cursor-pointer shadow-lg"
            title="Download"
          >
            <SvgIcon IconComponent={DownloadIcon} className="w-[24px] h-[24px] fill-transparent stroke-white" />
          </button>
        </div>
      )}
    </div>
  );
};
